import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Warning} from '../../icons';
import {PortalSparkline} from '../../portal_usage/PortalSparkline';
import {PortalUsageBadge} from '../../portal_usage/PortalUsageBadge';
import {
    chatIsOff,
    clientInitials,
    clientQueryCount,
    clientReportsGap,
    clientReportViewCount,
    clientSharedReportCount,
    daysSince,
    formatShortDate,
    relativeTimeLabel,
    REPORTS_GAP,
    reportsAreOff,
    USAGE_STATE,
} from '../../portal_usage/portalUsageState';

// La franja de color del borde izquierdo: el estado se lee antes que el texto, recorriendo la
// lista con la vista. Mismo lenguaje que la pastilla de la derecha, no uno nuevo.
const EDGE_BY_STATE = {
    [USAGE_STATE.ACTIVE]: 'ok',
    [USAGE_STATE.DORMANT]: 'warn',
    [USAGE_STATE.INVITED_UNUSED]: 'ia',
    [USAGE_STATE.NO_USERS]: 'muted',
    [USAGE_STATE.MODULE_OFF]: 'muted',
};

/**
 * Una fila del ranking de adopción.
 *
 * Entera clickeable hacia el tab Portal del cliente, que es donde está el detalle y la acción: la
 * pantalla contesta "¿a quién le hablo?" y el paso siguiente es siempre abrir a ese cliente.
 */
export function PortalUsageRow({client, agencyReportsModule}) {
    const {t, i18n} = useTranslation();

    const edge = EDGE_BY_STATE[client.state] || 'muted';
    const queries = clientQueryCount(client);
    const users = client.portal_users || 0;
    const reportViews = clientReportViewCount(client);
    const shared = clientSharedReportCount(client);
    const gap = clientReportsGap(client, {agencyReportsModule});

    return (
        <Link className={`pu-row -${edge}`} to={`/client/${client.client_id}/portal-config`}>
            <span className="pu-row__client">
                <span className="pu-row__avatar" aria-hidden="true">{clientInitials(client.name)}</span>
                <span className="pu-row__id">
                    {/* El nombre lo escribió la agencia: children de React, sin interpolar. */}
                    <span className="pu-row__name">{client.name}</span>
                    <span className="pu-row__sub">
                        {users === 0
                            ? t('portal_usage.rank_no_invited', 'sin usuarios invitados')
                            : (users === 1
                                ? t('portal_usage.rank_users_one', '1 usuario con acceso')
                                : t('portal_usage.rank_users_many', '{{n}} usuarios con acceso', {n: users}))}
                    </span>
                </span>
            </span>

            <span className="pu-row__state"><PortalUsageBadge state={client.state}/></span>

            {/* Un cero tiene que decir si es de demanda o de oferta: si el módulo está apagado no
                hubo nada que consultar, y un "0" ahí se leería como un cliente desinteresado.
                Sin «+N pasos» debajo (card k14eI6RC): no se entendía qué eran, y en una fila con
                0 consultas hacían que el cliente pareciera usarlo. */}
            {chatIsOff(client) ? (
                <span className="pu-row__num -off">
                    —
                    <span className="pu-row__num-sub">
                        {t('portal_usage.rank_chat_off', 'chat apagado')}
                    </span>
                </span>
            ) : (
                <span className={`pu-row__num${queries ? '' : ' -zero'}`}>{queries}</span>
            )}

            {reportsAreOff(client) ? (
                <span className="pu-row__num -off">
                    —
                    <span className="pu-row__num-sub">
                        {t('portal_usage.rank_reports_off', 'informes apagados')}
                    </span>
                </span>
            ) : (
                <span className={`pu-row__num${reportViews ? '' : ' -zero'}`}>{reportViews}</span>
            )}

            {/* Compartidos es un STOCK: enfrentado a los ingresos de al lado es lo que deja ver el
                caso caro — le compartiste cuatro informes y no abrió ninguno. */}
            {/* La marca es SÓLO para el toggle de este cliente. El bloqueo por plan lo dice el
                banner de arriba una vez: es la misma condición para las 72 filas, y repetirla
                convertiría en ruido justo la señal que distingue a una fila de las demás. */}
            <span className={`pu-row__num${shared ? '' : ' -zero'}`}>
                {shared}
                {gap === REPORTS_GAP.CLIENT_MODULE_OFF ? renderReportsGap(shared, t) : null}
            </span>

            <span className="pu-row__weeks">
                {/* Sin `|| []`: la prop es opt-in y `[]` NO es `undefined`. Con el fallback, un
                    backend viejo —que no manda `report_weeks`— entraba igual en modo dual y
                    dibujaba el sparkline del chat a mitad de alto, con una fila vacía debajo. */}
                <PortalSparkline weeks={client.weeks} reportWeeks={client.report_weeks}/>
            </span>

            <span className="pu-row__last">{lastLabel(client, t, i18n.language)}</span>
        </Link>
    );
}

/**
 * La marca de "compartiste y este cliente no lo ve".
 *
 * Va pegada al número de compartidos y no como estado, porque es ORTOGONAL al estado: un cliente
 * puede estar activo por el chat y tener la puerta de los informes cerrada al mismo tiempo.
 *
 * Sólo para el toggle del cliente: la fila entera ya linkea a la pantalla donde se prende. Nada
 * interactivo adentro — esto vive dentro de un `<Link>`, así que un botón acá sería un `<button>`
 * en un `<a>`.
 */
function renderReportsGap(shared, t) {
    const tip = t('portal_usage.rank_reports_blocked_tip',
        'Compartiste {{n}} informes con este cliente, pero el módulo de informes está apagado '
        + 'en su portal: nadie los ve. Se prende en la pestaña Portal de este cliente.',
        {n: shared});

    return (
        <span className="pu-row__gap">
            <Warning size={11} weight="fill"/>
            {t('portal_usage.rank_reports_blocked_pill', 'no los ve')}
            <span className="warning-tooltip -below-right -rich">
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text">{tip}</span>
                </span>
            </span>
        </span>
    );
}

/**
 * La columna "Última".
 *
 * Para el cliente dormido no alcanza con la fecha: "28 de mayo" obliga a hacer la cuenta. Lo que
 * decide si hay que llamarlo hoy es **cuánto hace**, así que van las dos cosas, y el "hace N días"
 * en ámbar. Para el que nunca usó no hay fecha que mostrar y un guion sería ambiguo: se dice.
 */
function lastLabel(client, t, language) {
    const relative = relativeTimeLabel(client.last_activity_at, t);

    if (!relative) {
        return (
            <span className="pu-row__last-none">
                {client.portal_users
                    ? t('portal_usage.rank_never_used', 'Nunca usó')
                    : t('portal_usage.rank_not_invited', 'Sin invitar')}
            </span>
        );
    }

    if (client.state === USAGE_STATE.DORMANT) {
        return (
            <>
                <b>{formatShortDate(client.last_activity_at, language)}</b>
                <span className="pu-row__last-warn">
                    {t('portal_usage.rank_dormant_days', 'hace {{n}} días',
                        {n: daysSince(client.last_activity_at)})}
                </span>
            </>
        );
    }

    return <b>{relative}</b>;
}

export default PortalUsageRow;
